(() => {
  class BEKProductEngine {
    constructor(data, options = {}) {
      this.data = data || {};
      this.options = {
        dealerDiscountPct: Number(options.dealerDiscountPct ?? 50),
        vatPct: Number(options.vatPct ?? 19),
        targetMarginPct: Number(options.targetMarginPct ?? 41),
        priceMode: options.priceMode || 'vd_list_round90',
        skyviewDealerDiscountPct: Number(options.skyviewDealerDiscountPct ?? options.dealerDiscountPct ?? 50),
        skyviewTargetMarginPct: Number(options.skyviewTargetMarginPct ?? 40),
        skyviewPriceMode: options.skyviewPriceMode || options.priceMode || 'vd_list_round90',
        flatlineDealerDiscountPct: Number(options.flatlineDealerDiscountPct ?? options.dealerDiscountPct ?? 50),
        flatlineTargetMarginPct: Number(options.flatlineTargetMarginPct ?? 40),
        flatlinePriceMode: options.flatlinePriceMode || options.priceMode || 'vd_list_round90',
        sg23DealerDiscountPct: Number(options.sg23DealerDiscountPct ?? 50),
        fr23DealerDiscountPct: Number(options.fr23DealerDiscountPct ?? 50),
        wedgeGlassDealerDiscountPct: Number(options.wedgeGlassDealerDiscountPct ?? 50),
        roundingMode: options.roundingMode || 'x090',
        mountingDealerDiscountPct: Number(options.mountingDealerDiscountPct ?? 50),
        mountingSurcharge6000: Number(options.mountingSurcharge6000 ?? 1290),
        mountingSurcharge7000: Number(options.mountingSurcharge7000 ?? 1490),
        mountingSurcharge8000: Number(options.mountingSurcharge8000 ?? 1690),
        mountingSurcharge9000: Number(options.mountingSurcharge9000 ?? 1790),
        mountingSurcharge10000: Number(options.mountingSurcharge10000 ?? 1890),
        mountingSurcharge11000: Number(options.mountingSurcharge11000 ?? 1990),
        mountingSurcharge12000: Number(options.mountingSurcharge12000 ?? 2090),
        mountingCombo6000: Number(options.mountingCombo6000 ?? 2490),
        mountingComboDiscountPctAbove6: Number(options.mountingComboDiscountPctAbove6 ?? 0),
        ledDealerDiscountPct: Number(options.ledDealerDiscountPct ?? 0),
        ledGross6: Number(options.ledGross6 ?? 149),
        ledGross8: Number(options.ledGross8 ?? 179),
        ledGross6wifi: Number(options.ledGross6wifi ?? 179),
        ledGross12wifi: Number(options.ledGross12wifi ?? 269),
        staticsDealerDiscountPct: Number(options.staticsDealerDiscountPct ?? 0),
        staticsGrossCalculation: Number(options.staticsGrossCalculation ?? 249),
        staticsGrossStamp: Number(options.staticsGrossStamp ?? 990),
        sg23PriceMode: options.sg23PriceMode || 'approved_prices',
        sg23TargetMarginPct: Number(options.sg23TargetMarginPct ?? 41),
        sg23Gross3000: Number(options.sg23Gross3000 ?? 1890),
        sg23Gross4000: Number(options.sg23Gross4000 ?? 2490),
        sg23Gross5000: Number(options.sg23Gross5000 ?? 3190),
        sg23Gross6000: Number(options.sg23Gross6000 ?? 3590),
        fr23PriceMode: options.fr23PriceMode || 'vd_list_rounding',
        fr23TargetMarginPct: Number(options.fr23TargetMarginPct ?? 41),
        fr23StandaloneMinContributionNet: Number(options.fr23StandaloneMinContributionNet ?? 250)
      };
    }

    findCeilingBand(value, bands) {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      return bands.find((band) => n <= Number(band)) ?? null;
    }

    roundUpTo90(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return 0;
      return Math.ceil((n + 10) / 100) * 100 - 10;
    }

    roundUpTo10(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return 0;
      return Math.ceil(n / 10) * 10;
    }

    roundToNearestEuro(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return 0;
      return Math.round(n);
    }

    applyGrossRounding(value) {
      if (this.options.roundingMode === 'x010') return this.roundUpTo10(value);
      if (this.options.roundingMode === 'euro') return this.roundToNearestEuro(value);
      return this.roundUpTo90(value);
    }

    grossFromTargetMargin(bekEkNet, targetMarginPct) {
      const vatRate = this.options.vatPct / 100;
      const marginRate = Number(targetMarginPct || 0) / 100;
      const targetNet = marginRate >= 1 ? Number(bekEkNet || 0) : Number(bekEkNet || 0) / (1 - marginRate);
      return this.applyGrossRounding(targetNet * (1 + vatRate));
    }

    mountingSingleSurchargeForWidth(widthMm) {
      const bands = [6000, 7000, 8000, 9000, 10000, 11000, 12000];
      const band = bands.find((b) => Number(widthMm) <= b);
      if (!band) return null;
      return Number(this.options[`mountingSurcharge${band}`]);
    }

    sg23ApprovedOverride(variantKey, widthBand, heightBand) {
      if (Number(heightBand) !== 2500) return null;
      const map = {
        '3r3d:3000': this.options.sg23Gross3000,
        '4r4d:4000': this.options.sg23Gross4000,
        '5r5d:5000': this.options.sg23Gross5000,
        '3r6d:6000': this.options.sg23Gross6000
      };
      const value = Number(map[`${variantKey}:${widthBand}`]);
      return Number.isFinite(value) && value > 0 ? value : null;
    }

    calculateTds({ width, depth, glass = 'clear' }) {
      const tds = this.data?.tds;
      if (!tds) return { ok: false, error: 'TDS_PRICE_DATA_MISSING' };

      const requestedWidth = Number(width);
      const requestedDepth = Number(depth);
      const priceKey = tds.coverPriceKeys?.[glass] || glass;
      const coverWidthBands = tds.coverWidthBands?.[priceKey] || tds.widthBands || [];
      const widthBand = this.findCeilingBand(requestedWidth, coverWidthBands);
      const depthBand = this.findCeilingBand(requestedDepth, tds.depthBands || []);
      if (!widthBand || !depthBand) {
        return { ok: false, error: 'OUTSIDE_TDS_MATRIX', requestedWidth, requestedDepth, glass, priceKey };
      }

      const depthIndex = tds.depthBands.indexOf(depthBand);
      const list = tds.pricesNetList?.[priceKey]?.[String(widthBand)];
      const vdListNet = Number(list?.[depthIndex]);
      if (!Number.isFinite(vdListNet)) {
        return { ok: false, error: 'TDS_PRICE_NOT_FOUND', requestedWidth, requestedDepth, widthBand, depthBand, glass, priceKey };
      }

      const discountRate = this.options.dealerDiscountPct / 100;
      const vatRate = this.options.vatPct / 100;
      const targetMarginRate = this.options.targetMarginPct / 100;
      const bekEkNet = vdListNet * (1 - discountRate);

      let rawGross;
      if (this.options.priceMode === 'target_margin') {
        const targetNet = targetMarginRate >= 1 ? bekEkNet : bekEkNet / (1 - targetMarginRate);
        rawGross = targetNet * (1 + vatRate);
      } else {
        rawGross = vdListNet;
      }
      const customerGross = this.applyGrossRounding(rawGross);
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;

      const rules = tds.technicalRules || {};
      const flags = [];
      if (
        requestedDepth >= Number(rules.rafterCoreRequiredFromDepthMm || Infinity) &&
        rules.rafterCoreIncludedInMatrixPrice === false
      ) {
        flags.push({ code: 'RAFTER_CORE_REQUIRED_NOT_PRICED', message: rules.note || 'Verstärkungsprofil erforderlich und noch nicht eingerechnet.' });
      }
      if (rules.deliveryIncluded === false) {
        flags.push({ code: 'DELIVERY_NOT_INCLUDED', message: 'Lieferung ist im TDS-Matrixpreis nicht enthalten.' });
      }

      return {
        ok: true,
        requestedWidth,
        requestedDepth,
        widthBand,
        depthBand,
        glass,
        glassLabel: tds.glassLabels?.[glass] || glass,
        coverKind: priceKey === 'poly' ? 'poly' : 'glass',
        priceKey,
        vdListNet,
        dealerDiscountPct: this.options.dealerDiscountPct,
        bekEkNet,
        vatPct: this.options.vatPct,
        priceMode: this.options.priceMode,
        targetMarginPct: this.options.targetMarginPct,
        customerNet,
        customerGross,
        grossProfitNet,
        rawMarginPct,
        flags
      };
    }


    calculateSkyView({ width, depth, cover = 'clear' }) {
      const cfg = this.data?.skyview;
      if (!cfg) return { ok: false, error: 'SKYVIEW_PRICE_DATA_MISSING' };

      const requestedWidth = Number(width);
      const requestedDepth = Number(depth);
      const coverCfg = cfg.covers?.[String(cover)];
      if (!coverCfg) return { ok: false, error: 'SKYVIEW_COVER_INVALID', cover };

      const matrix = cfg.matrices?.[coverCfg.kind];
      if (!matrix) return { ok: false, error: 'SKYVIEW_MATRIX_MISSING', kind: coverCfg.kind };
      const widthBand = this.findCeilingBand(requestedWidth, matrix.widthBands || []);
      const depthBand = this.findCeilingBand(requestedDepth, matrix.depthBands || []);
      if (!widthBand || !depthBand) {
        return { ok: false, error: 'OUTSIDE_SKYVIEW_MATRIX', requestedWidth, requestedDepth, cover };
      }

      const row = matrix.pricesNetList?.[`${widthBand}x${depthBand}`];
      const vdListNet = Number(row?.[coverCfg.priceKey]);
      if (!Number.isFinite(vdListNet)) {
        return { ok: false, error: 'SKYVIEW_PRICE_NOT_FOUND', requestedWidth, requestedDepth, widthBand, depthBand, cover };
      }

      const discountRate = this.options.skyviewDealerDiscountPct / 100;
      const vatRate = this.options.vatPct / 100;
      const targetMarginRate = this.options.skyviewTargetMarginPct / 100;
      const bekEkNet = vdListNet * (1 - discountRate);
      let rawGross;
      if (this.options.skyviewPriceMode === 'target_margin') {
        const targetNet = targetMarginRate >= 1 ? bekEkNet : bekEkNet / (1 - targetMarginRate);
        rawGross = targetNet * (1 + vatRate);
      } else {
        rawGross = vdListNet;
      }
      const customerGross = this.applyGrossRounding(rawGross);
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;

      const structure = cfg.structure?.[coverCfg.kind]?.[String(widthBand)] || {};
      const rules = cfg.technicalRules || {};
      const flags = [];
      if (
        requestedDepth >= Number(rules.rafterCoreRequiredFromDepthMm || Infinity) &&
        rules.rafterCoreIncludedInMatrixPrice === false
      ) {
        flags.push({ code: 'SKYVIEW_RAFTER_CORE_REQUIRED_NOT_PRICED', message: 'SkyView Innenkern Sparren ist für diese Tiefe erforderlich und nicht automatisch im Matrixpreis enthalten.' });
      }
      if (requestedWidth > Number(rules.gutterCoreRequiredAboveWidthMm || Infinity) && rules.gutterCoreIncludedInMatrixPrice === false) {
        flags.push({ code: 'SKYVIEW_GUTTER_CORE_REQUIRED_NOT_PRICED', message: 'SkyView Innenkern Rinne ist bei Breiten über 7 m erforderlich und nicht automatisch im Matrixpreis enthalten.' });
      }
      if (coverCfg.kind === 'glass' && requestedWidth >= Number(rules.splitGlassNoteFromWidthMm || Infinity)) {
        flags.push({ code: 'SKYVIEW_SPLIT_GLASS', message: 'Laut VD-Preisliste gilt der Preis in diesem Breitenbereich nur für geteiltes Glas.' });
      }
      if (rules.deliveryIncluded === false) {
        flags.push({ code: 'DELIVERY_NOT_INCLUDED', message: 'VD-Lieferung ist im SkyView-Matrixpreis nicht enthalten; BEK Versandlogik wird separat angewendet.' });
      }

      return {
        ok: true,
        productCode: 'SKYVIEW',
        productLabel: 'SkyView kubisches Terrassendach',
        requestedWidth,
        requestedDepth,
        widthBand,
        depthBand,
        cover: String(cover),
        coverLabel: coverCfg.label || String(cover),
        coverKind: coverCfg.kind,
        vdListNet,
        dealerDiscountPct: this.options.skyviewDealerDiscountPct,
        bekEkNet,
        vatPct: this.options.vatPct,
        priceMode: this.options.skyviewPriceMode,
        targetMarginPct: this.options.skyviewTargetMarginPct,
        customerNet,
        customerGross,
        grossProfitNet,
        rawMarginPct,
        rafterCount: Number(structure.rafters || 0),
        supportCount: Number(structure.supports || 0),
        flags
      };
    }


    calculateFlatLine({ width, depth, cover = 'standard' }) {
      const cfg = this.data?.flatline;
      if (!cfg) return { ok: false, error: 'FLATLINE_PRICE_DATA_MISSING' };

      const requestedWidth = Number(width);
      const requestedDepth = Number(depth);
      const coverCfg = cfg.covers?.[String(cover)];
      if (!coverCfg) return { ok: false, error: 'FLATLINE_COVER_INVALID', cover };

      const widthBand = this.findCeilingBand(requestedWidth, cfg.widthBands || []);
      const depthBand = this.findCeilingBand(requestedDepth, cfg.depthBands || []);
      if (!widthBand || !depthBand) {
        return { ok: false, error: 'OUTSIDE_FLATLINE_MATRIX', requestedWidth, requestedDepth, cover };
      }
      const row = cfg.pricesNetList?.[`${widthBand}x${depthBand}`];
      const vdListNet = Number(row?.[coverCfg.priceKey]);
      if (!Number.isFinite(vdListNet)) {
        return { ok: false, error: 'FLATLINE_PRICE_NOT_FOUND', requestedWidth, requestedDepth, widthBand, depthBand, cover };
      }

      const discountRate = this.options.flatlineDealerDiscountPct / 100;
      const vatRate = this.options.vatPct / 100;
      const targetMarginRate = this.options.flatlineTargetMarginPct / 100;
      const bekEkNet = vdListNet * (1 - discountRate);
      let rawGross;
      if (this.options.flatlinePriceMode === 'target_margin') {
        const targetNet = targetMarginRate >= 1 ? bekEkNet : bekEkNet / (1 - targetMarginRate);
        rawGross = targetNet * (1 + vatRate);
      } else {
        rawGross = vdListNet;
      }
      const customerGross = this.applyGrossRounding(rawGross);
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;

      const structural = cfg.structure?.[String(widthBand)] || {};
      const rules = cfg.technicalRules || {};
      const flags = [];
      if (requestedDepth >= Number(rules.rafterCoreRequiredFromDepthMm || Infinity) && rules.rafterCoreIncludedInMatrixPrice === false) {
        flags.push({ code:'FLATLINE_RAFTER_CORE_REQUIRED_NOT_PRICED', message:'Flat Line benötigt für diese Tiefe zusätzliche Sparren-Innenkerne. Diese sind noch nicht automatisch bepreist.' });
      }
      if (requestedWidth > Number(rules.gutterCoreRequiredAboveWidthMm || Infinity) && rules.gutterCoreIncludedInMatrixPrice === false) {
        flags.push({ code:'FLATLINE_GUTTER_CORE_REQUIRED_NOT_PRICED', message:'Flat Line benötigt bei dieser Breite einen zusätzlichen Rinnen-Innenkern. Dieser ist noch nicht automatisch bepreist.' });
      }
      if (rules.deliveryIncluded === false) {
        flags.push({ code:'DELIVERY_NOT_INCLUDED', message:'VD-Lieferung ist im Flat-Line-Matrixpreis nicht enthalten; BEK Lieferung wird separat mit 0 € angewendet.' });
      }

      return {
        ok:true,
        productCode:'FLATLINE',
        productLabel:'Flat Line Carport',
        requestedWidth, requestedDepth, widthBand, depthBand,
        cover:String(cover), coverLabel:coverCfg.label || String(cover),
        vdListNet,
        dealerDiscountPct:this.options.flatlineDealerDiscountPct,
        bekEkNet,
        vatPct:this.options.vatPct,
        priceMode:this.options.flatlinePriceMode,
        targetMarginPct:this.options.flatlineTargetMarginPct,
        customerNet, customerGross, grossProfitNet, rawMarginPct,
        rafterCount:Number(structural.rafters || 0),
        supportCount:Number(structural.supports || 0),
        frontPostCount:Number(structural.frontPosts || 0),
        rearPostCount:Number(structural.rearPosts || 0),
        flags
      };
    }

    calculateMountingConstruction({ width, mode = 'wall', combo = false }) {
      const cfg = this.data?.mountingConstruction;
      if (!cfg) return { ok: false, error: 'MOUNTING_DATA_MISSING' };

      const widthMm = Number(width);
      if (!Number.isFinite(widthMm) || widthMm <= 0) {
        return { ok: false, error: 'INVALID_WIDTH', widthMm };
      }

      if (mode === 'wall' && !combo) {
        return {
          ok: true,
          mode: 'wall',
          combo: false,
          widthMm,
          customerGrossSurcharge: 0,
          vdListNet: 0,
          bekEkNet: 0,
          customerNet: 0,
          grossProfitNet: 0,
          rawMarginPct: 0,
          quantity: { constructionRuns: 0, beamPieces: 0, beamTotalM: 0, fasciaTotalM: 0, posts: 0, postTotalM: 0 },
          components: [],
          flags: []
        };
      }

      const rules = cfg.rules || {};
      if (widthMm > Number(rules.maxWidthMm || 12000)) {
        return { ok: false, error: 'MOUNTING_WIDTH_OUTSIDE_RULES', widthMm };
      }

      const singleBand = (cfg.customerGrossSurchargeBands || []).find((band) => widthMm <= Number(band.maxWidthMm));
      if (!singleBand) return { ok: false, error: 'MOUNTING_SURCHARGE_NOT_FOUND', widthMm };

      const isSplit = widthMm > Number(rules.splitAboveMm || 6000);
      const constructionRuns = combo ? Number(rules.comboMultiplier || 2) : 1;
      const postsPerRun = isSplit ? Number(rules.postsAbove6m || 3) : Number(rules.postsUpTo6m || 2);
      const postLengthM = Number(rules.postLengthM || 3);
      const beamPiecesPerRun = isSplit ? 2 : 1;
      const beamTotalM = (widthMm / 1000) * constructionRuns;
      const fasciaTotalM = (widthMm / 1000) * constructionRuns;
      const posts = postsPerRun * constructionRuns;
      const postTotalM = posts * postLengthM;

      const prices = cfg.componentsNetListPerM || {};
      const beamList = beamTotalM * Number(prices.staticBeam?.price || 0);
      const fasciaList = fasciaTotalM * Number(prices.staticBeamFascia?.price || 0);
      const postsList = postTotalM * Number(prices.post110?.price || 0);
      const vdListNet = beamList + fasciaList + postsList;
      const discountPct = Number(this.options.mountingDealerDiscountPct ?? cfg.dealerDiscountPct ?? this.options.dealerDiscountPct);
      const bekEkNet = vdListNet * (1 - discountPct / 100);

      const centralSingle = this.mountingSingleSurchargeForWidth(widthMm);
      let customerGrossSurcharge;
      if (combo) {
        if (widthMm <= 6000) {
          customerGrossSurcharge = Number(this.options.mountingCombo6000);
        } else {
          const comboDiscount = Math.max(0, Math.min(100, Number(this.options.mountingComboDiscountPctAbove6 || 0))) / 100;
          customerGrossSurcharge = Number(centralSingle || singleBand.single || 0) * 2 * (1 - comboDiscount);
          customerGrossSurcharge = this.applyGrossRounding(customerGrossSurcharge);
        }
      } else {
        customerGrossSurcharge = Number(centralSingle ?? singleBand.single ?? 0);
      }

      const vatRate = this.options.vatPct / 100;
      const customerNet = customerGrossSurcharge / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;
      const flags = [];

      if (isSplit && rules.middlePostAtBeamJointAbove6m) {
        flags.push({ code: 'SPLIT_BEAM_MIDDLE_POST', message: `Statikträger je Konstruktion in 2 × ${(widthMm / 2000).toFixed(3).replace('.', ',')} m teilen; mittlerer Pfosten unter der Teilung.` });
      }
      if (combo && widthMm > 6000) {
        const comboDiscount = Number(this.options.mountingComboDiscountPctAbove6 || 0);
        flags.push({ code: 'COMBO_CENTRAL_RULE_ABOVE_6M', message: comboDiscount > 0 ? `Über 6 m: zentraler Kombivorteil ${comboDiscount.toFixed(1).replace('.', ',')} % auf 2 × Einzelaufschlag.` : 'Über 6 m: zentral aktuell 2 × Einzelaufschlag ohne Kombivorteil.' });
      }
      flags.push({ code: 'NO_UNCONFIRMED_CONNECTORS', message: 'Zusätzliche Kopplungs-/Verbindungsbauteile sind nicht eingerechnet, solange dafür keine bestätigte BEK-Regel vorliegt.' });

      const label = combo ? 'Freistehend + Dachüberstand' : (mode === 'overhang' ? 'Dachüberstand' : 'Freistehend');
      return {
        ok: true,
        mode,
        label,
        combo,
        widthMm,
        isSplit,
        surchargeBandMaxWidthMm: Number(singleBand.maxWidthMm),
        dealerDiscountPct: discountPct,
        customerGrossSurcharge,
        customerNet,
        vdListNet,
        bekEkNet,
        grossProfitNet,
        rawMarginPct,
        quantity: {
          constructionRuns,
          beamPieces: beamPiecesPerRun * constructionRuns,
          beamPiecesPerRun,
          beamPieceLengthM: isSplit ? widthMm / 2000 : widthMm / 1000,
          beamTotalM,
          fasciaTotalM,
          posts,
          postLengthM,
          postTotalM
        },
        components: [
          { key: 'staticBeam', label: prices.staticBeam?.label || 'StatikTräger', article: prices.staticBeam?.article || '', quantityM: beamTotalM, listPricePerM: Number(prices.staticBeam?.price || 0), vdListNet: beamList },
          { key: 'staticBeamFascia', label: prices.staticBeamFascia?.label || 'StatikTrägerblende', article: prices.staticBeamFascia?.article || '', quantityM: fasciaTotalM, listPricePerM: Number(prices.staticBeamFascia?.price || 0), vdListNet: fasciaList },
          { key: 'post110', label: prices.post110?.label || 'Pfosten 110 × 110', article: prices.post110?.article || '', quantityM: postTotalM, listPricePerM: Number(prices.post110?.price || 0), vdListNet: postsList }
        ],
        flags
      };
    }

    calculateEquipmentOption({ category, value }) {
      const cfg = this.data?.equipment?.[category];
      if (!cfg) return { ok: false, error: 'EQUIPMENT_CATEGORY_MISSING', category, value };

      const option = cfg.options?.[String(value)];
      if (!option) return { ok: false, error: 'EQUIPMENT_OPTION_MISSING', category, value };

      const vdListNet = Number(option.vdListNet || 0);
      let discountPct = Number(option.dealerDiscountPct ?? cfg.dealerDiscountPct ?? 0);
      let customerGrossSurcharge = Number(option.customerGross || 0);
      if (category === 'led') {
        discountPct = Number(this.options.ledDealerDiscountPct ?? discountPct);
        const ledPriceMap = {
          'none': 0,
          '6': this.options.ledGross6,
          '8': this.options.ledGross8,
          '6wifi': this.options.ledGross6wifi,
          '12wifi': this.options.ledGross12wifi
        };
        customerGrossSurcharge = Number(ledPriceMap[String(value)] ?? customerGrossSurcharge);
      }
      if (category === 'statics') {
        discountPct = Number(this.options.staticsDealerDiscountPct ?? discountPct);
        const staticsPriceMap = {
          'simple': 0,
          'calculation': this.options.staticsGrossCalculation,
          'stamp': this.options.staticsGrossStamp
        };
        customerGrossSurcharge = Number(staticsPriceMap[String(value)] ?? customerGrossSurcharge);
      }
      const bekEkNet = vdListNet * (1 - discountPct / 100);
      const vatRate = this.options.vatPct / 100;
      const customerNet = customerGrossSurcharge / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;
      const flags = [];

      if (category === 'led' && value !== 'none' && cfg.automaticAccessoryRulesConfirmed === false) {
        flags.push({
          code: 'LED_ACCESSORY_RULE_PENDING',
          message: 'LED-Trafo / Verlängerungskabel sind als VD-Zubehör hinterlegt, werden aber erst nach bestätigter technischer Zuordnungsregel automatisch ergänzt.'
        });
      }
      if (category === 'statics' && value !== 'simple' && cfg.discountConfirmed === false) {
        flags.push({
          code: 'STATICS_DISCOUNT_UNCONFIRMED',
          message: 'Statik-Händlerrabatt ist noch nicht bestätigt; BEK-EK wird in M9.3 konservativ mit 0 % Rabatt gerechnet.'
        });
      }

      return {
        ok: true,
        category,
        value: String(value),
        label: option.label || String(value),
        article: option.article || '',
        vdListNet,
        dealerDiscountPct: discountPct,
        bekEkNet,
        vatPct: this.options.vatPct,
        customerGrossSurcharge,
        customerNet,
        grossProfitNet,
        rawMarginPct,
        flags
      };
    }

    calculateSg23({ width, height = 2300, glass = 'clear', application = 'outdoor', variant = 'auto' }) {
      const cfg = this.data?.sideWalls?.sg23;
      if (!cfg) return { ok: false, error: 'SG23_DATA_MISSING' };

      const requestedWidth = Number(width);
      const requestedHeight = Number(height);
      if (!Number.isFinite(requestedWidth) || requestedWidth <= 0 || !Number.isFinite(requestedHeight) || requestedHeight <= 0) {
        return { ok: false, error: 'INVALID_SG23_DIMENSIONS', requestedWidth, requestedHeight };
      }

      const maxActualHeight = application === 'indoor'
        ? Number(cfg.maxIndoorActualHeightMm || 2600)
        : Number(cfg.maxOutdoorActualHeightMm || 2450);
      if (requestedHeight > maxActualHeight) {
        return {
          ok: false,
          error: 'SG23_HEIGHT_EXCEEDS_LIMIT',
          requestedWidth,
          requestedHeight,
          maxActualHeight
        };
      }

      let variantKey = variant;
      if (!variantKey || variantKey === 'auto') {
        const rule = (cfg.autoLayoutRules || []).find((item) => requestedWidth <= Number(item.maxWidthMm));
        variantKey = rule?.variant || null;
      }
      const variantCfg = cfg.variants?.[variantKey];
      if (!variantCfg) {
        return { ok: false, error: 'SG23_AUTO_LAYOUT_NOT_AVAILABLE', requestedWidth, requestedHeight, variantKey };
      }

      const widthBand = this.findCeilingBand(requestedWidth, variantCfg.widthBands || []);
      const heightBand = this.findCeilingBand(requestedHeight, cfg.heightBands || []);
      if (!widthBand || !heightBand) {
        return {
          ok: false,
          error: 'SG23_OUTSIDE_VARIANT_MATRIX',
          requestedWidth,
          requestedHeight,
          variantKey
        };
      }

      const widthIndex = (variantCfg.widthBands || []).indexOf(widthBand);
      const baseVdListNet = Number(variantCfg.pricesNetList?.[String(heightBand)]?.[widthIndex]);
      if (!Number.isFinite(baseVdListNet)) {
        return { ok: false, error: 'SG23_PRICE_NOT_FOUND', requestedWidth, requestedHeight, variantKey, widthBand, heightBand };
      }

      const actualAreaSqm = (requestedWidth / 1000) * (requestedHeight / 1000);
      const satinatoSurchargeNetList = glass === 'satinato'
        ? actualAreaSqm * Number(cfg.satinatoSurchargeNetListPerSqm || 0)
        : 0;
      const vdListNet = baseVdListNet + satinatoSurchargeNetList;
      const discountPct = Number(this.options.sg23DealerDiscountPct ?? cfg.dealerDiscountPct ?? 50);
      const bekEkNet = vdListNet * (1 - discountPct / 100);

      const overrideKey = `${variantKey}:${widthBand}:${heightBand}`;
      let customerGross;
      if (this.options.sg23PriceMode === 'target_margin') {
        customerGross = this.grossFromTargetMargin(bekEkNet, this.options.sg23TargetMarginPct);
      } else if (this.options.sg23PriceMode === 'vd_list_rounding') {
        customerGross = this.applyGrossRounding(baseVdListNet);
      } else {
        customerGross = this.sg23ApprovedOverride(variantKey, widthBand, heightBand);
        if (!Number.isFinite(customerGross)) customerGross = Number(cfg.standardGrossOverrides?.[overrideKey]);
        if (!Number.isFinite(customerGross)) customerGross = this.applyGrossRounding(baseVdListNet);
      }
      if (glass === 'satinato' && satinatoSurchargeNetList > 0 && this.options.sg23PriceMode !== 'target_margin') {
        customerGross += this.roundUpTo10(satinatoSurchargeNetList);
      }

      const vatRate = this.options.vatPct / 100;
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;
      const flags = [];

      if (requestedWidth > 6500) {
        flags.push({ code: 'SG23_FRONT_SPLIT_PENDING', message: 'Breiten über 6.500 mm benötigen eine noch nicht freigegebene Mehranlagen-/Teilungslogik.' });
      }
      if (heightBand === 2500 && requestedHeight <= maxActualHeight) {
        flags.push({ code: 'SG23_2500_PRICE_BAND', message: `Tatsächliche Höhe ${requestedHeight} mm wird in der 2.500-mm-Preisstaffel berechnet.` });
      }

      return {
        ok: true,
        requestedWidth,
        requestedHeight,
        application,
        variant: variantKey,
        layoutLabel: variantCfg.label,
        rails: Number(variantCfg.rails || 0),
        doors: Number(variantCfg.doors || 0),
        widthBand,
        heightBand,
        glass,
        glassLabel: glass === 'satinato' ? '10 mm ESG Satinato' : '10 mm ESG klar',
        actualAreaSqm,
        baseVdListNet,
        satinatoSurchargeNetList,
        vdListNet,
        dealerDiscountPct: discountPct,
        priceMode: this.options.sg23PriceMode,
        targetMarginPct: this.options.sg23TargetMarginPct,
        bekEkNet,
        customerGross,
        customerNet,
        grossProfitNet,
        rawMarginPct,
        flags
      };
    }

    selectFr23WedgeVariant(widthMm) {
      const cfg = this.data?.sideWalls?.fr23Wedge;
      const rule = (cfg?.bekAutoLayoutRules || []).find((item) => widthMm <= Number(item.maxWidthMm));
      return rule?.variant || null;
    }

    calculateFr23Wedge({ width, heightLow, heightHigh, glass = 'clear', standalone = false, variant = 'auto' }) {
      const cfg = this.data?.sideWalls?.fr23Wedge;
      if (!cfg) return { ok: false, error: 'FR23_WEDGE_DATA_MISSING' };

      const requestedWidth = Number(width);
      if (heightLow === null || heightLow === undefined || heightLow === '' || heightHigh === null || heightHigh === undefined || heightHigh === '') {
        return { ok: false, error: 'WEDGE_HEIGHTS_REQUIRED', requestedWidth };
      }
      const requestedLow = Number(heightLow);
      const requestedHigh = Number(heightHigh);
      if (!Number.isFinite(requestedWidth) || requestedWidth <= 0) {
        return { ok: false, error: 'INVALID_FR23_WIDTH', requestedWidth };
      }
      if (!Number.isFinite(requestedLow) || !Number.isFinite(requestedHigh)) {
        return { ok: false, error: 'WEDGE_HEIGHTS_REQUIRED', requestedWidth };
      }

      const minSide = Number(cfg.minElementSideHeightMm || 150);
      if (requestedLow < minSide || requestedHigh < minSide) {
        return {
          ok: false,
          error: 'FR23_SIDE_HEIGHT_BELOW_MINIMUM',
          requestedWidth,
          requestedLow,
          requestedHigh,
          minSide
        };
      }

      const maxHeight = Math.max(requestedLow, requestedHigh);
      if (maxHeight > Number(cfg.maxMatrixHeightMm || 2500)) {
        return {
          ok: false,
          error: 'FR23_HEIGHT_OUTSIDE_MATRIX',
          requestedWidth,
          requestedLow,
          requestedHigh,
          maxHeight
        };
      }

      let variantKey = variant;
      if (!variantKey || variantKey === 'auto') variantKey = this.selectFr23WedgeVariant(requestedWidth);
      const variantCfg = cfg.variants?.[variantKey];
      if (!variantCfg) return { ok: false, error: 'FR23_AUTO_LAYOUT_NOT_AVAILABLE', requestedWidth, maxHeight, variantKey };

      const widthBand = this.findCeilingBand(requestedWidth, variantCfg.widthBands || []);
      const heightBand = this.findCeilingBand(maxHeight, variantCfg.heightBands || []);
      if (!widthBand || !heightBand) {
        return {
          ok: false,
          error: 'FR23_OUTSIDE_VARIANT_MATRIX',
          requestedWidth,
          maxHeight,
          variantKey
        };
      }

      const widthIndex = (variantCfg.widthBands || []).indexOf(widthBand);
      const frameVdListNet = Number(variantCfg.pricesNetList?.[String(heightBand)]?.[widthIndex]);
      if (!Number.isFinite(frameVdListNet)) {
        return {
          ok: false,
          error: 'FR23_PRICE_NOT_FOUND',
          requestedWidth,
          maxHeight,
          variantKey,
          widthBand,
          heightBand
        };
      }

      const glassAreaSqm = (requestedWidth / 1000) * ((requestedLow + requestedHigh) / 2000);
      const glassListPerSqm = Number(cfg.glassNetListPerSqm?.[glass] || 0);
      const glassVdListNet = glassAreaSqm * glassListPerSqm;
      const vdListNet = frameVdListNet + glassVdListNet;

      const frameDiscountPct = Number(this.options.fr23DealerDiscountPct ?? cfg.frameDealerDiscountPct ?? 50);
      const glassDiscountPct = Number(this.options.wedgeGlassDealerDiscountPct ?? cfg.glassDealerDiscountPct ?? 50);
      const frameBekEkNet = frameVdListNet * (1 - frameDiscountPct / 100);
      const glassBekEkNet = glassVdListNet * (1 - glassDiscountPct / 100);
      const bekEkNet = frameBekEkNet + glassBekEkNet;

      const vatRate = this.options.vatPct / 100;
      let customerGross = this.options.fr23PriceMode === 'target_margin'
        ? this.grossFromTargetMargin(bekEkNet, this.options.fr23TargetMarginPct)
        : this.applyGrossRounding(vdListNet);
      if (standalone) {
        const minContributionNet = Number(this.options.fr23StandaloneMinContributionNet ?? cfg.standaloneMinContributionNet ?? 0);
        const minGrossForContribution = (bekEkNet + minContributionNet) * (1 + vatRate);
        customerGross = Math.max(customerGross, this.applyGrossRounding(minGrossForContribution));
      }

      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;

      return {
        ok: true,
        requestedWidth,
        requestedLow,
        requestedHigh,
        maxHeight,
        variant: variantKey,
        layoutLabel: variantCfg.label,
        widthBand,
        heightBand,
        glass,
        glassLabel: cfg.glassLabels?.[glass] || glass,
        glassAreaSqm,
        glassListPerSqm,
        frameVdListNet,
        glassVdListNet,
        vdListNet,
        frameDealerDiscountPct: frameDiscountPct,
        glassDealerDiscountPct: glassDiscountPct,
        frameBekEkNet,
        glassBekEkNet,
        bekEkNet,
        standalone,
        priceMode: this.options.fr23PriceMode,
        targetMarginPct: this.options.fr23TargetMarginPct,
        customerGross,
        customerNet,
        grossProfitNet,
        rawMarginPct,
        flags: [
          {
            code: 'BEK_FR23_AUTO_LAYOUT',
            message: `${variantCfg.label}; automatische Sprossenwahl ist BEK V1 Layoutlogik.`
          }
        ]
      };
    }

    calculateTdsFrontWall({ roofWidth, sgHeight = 2300, sgGlass = 'clear' }) {
      const cfg = this.data?.sideWalls?.sg23;
      if (!cfg) return { ok: false, error: 'SG23_DATA_MISSING' };

      const requestedRoofWidth = Number(roofWidth);
      if (!Number.isFinite(requestedRoofWidth) || requestedRoofWidth <= 0) {
        return { ok: false, error: 'INVALID_TDS_FRONT_WIDTH', requestedRoofWidth };
      }

      const rule = cfg.tdsFrontRule || {};
      const splitAbove = Number(rule.splitAboveRoofWidthMm || 4000);
      const postWidthMm = Number(rule.postWidthMm || 110);
      const split = requestedRoofWidth > splitAbove;
      const fieldCount = split ? Number(rule.fieldsAboveThreshold || 2) : Number(rule.fieldsUpToThreshold || 1);
      const postCount = split ? Number(rule.postsAboveThreshold || 3) : Number(rule.postsUpToThreshold || 2);
      const clearTotalWidthMm = requestedRoofWidth - (postCount * postWidthMm);
      const fieldWidthMm = clearTotalWidthMm / fieldCount;

      if (!(fieldWidthMm > 0)) {
        return {
          ok: false,
          error: 'TDS_FRONT_CLEAR_WIDTH_INVALID',
          requestedRoofWidth,
          fieldCount,
          postCount,
          postWidthMm,
          clearTotalWidthMm
        };
      }

      const fieldQuote = this.calculateSg23({
        width: fieldWidthMm,
        height: sgHeight,
        glass: sgGlass,
        application: 'outdoor',
        variant: 'auto'
      });
      if (!fieldQuote.ok) {
        return {
          ok: false,
          error: fieldQuote.error,
          requestedRoofWidth,
          fieldCount,
          postCount,
          postWidthMm,
          clearTotalWidthMm,
          fieldWidthMm,
          fieldQuote
        };
      }

      const vdListNet = fieldQuote.vdListNet * fieldCount;
      const bekEkNet = fieldQuote.bekEkNet * fieldCount;
      const customerGross = fieldQuote.customerGross * fieldCount;
      const vatRate = this.options.vatPct / 100;
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;
      const layoutLabel = fieldCount === 1
        ? `1 Frontfeld · ${fieldQuote.layoutLabel}`
        : `${fieldCount} Frontfelder · je ${fieldQuote.layoutLabel}`;

      const flags = [
        ...(fieldQuote.flags || []),
        {
          code: split ? 'TDS_FRONT_TWO_FIELDS' : 'TDS_FRONT_ONE_FIELD',
          message: split
            ? `TDS über 4.000 mm: 3 Frontpfosten erzeugen 2 lichte Felder. Kalkulation: 2 separate SG23 à ca. ${Math.round(fieldWidthMm)} mm.`
            : `TDS bis 4.000 mm: 2 Frontpfosten erzeugen 1 lichtes Feld. Kalkulation: 1 SG23 mit ca. ${Math.round(fieldWidthMm)} mm.`
        }
      ];

      return {
        ok: true,
        requestedRoofWidth,
        split,
        fieldCount,
        postCount,
        postWidthMm,
        clearTotalWidthMm,
        fieldWidthMm,
        fieldQuote,
        fields: Array.from({ length: fieldCount }, (_, index) => ({ index: index + 1, ...fieldQuote })),
        layoutLabel,
        widthBand: fieldQuote.widthBand,
        heightBand: fieldQuote.heightBand,
        glass: fieldQuote.glass,
        glassLabel: fieldQuote.glassLabel,
        vdListNet,
        dealerDiscountPct: fieldQuote.dealerDiscountPct,
        bekEkNet,
        customerGross,
        customerNet,
        grossProfitNet,
        rawMarginPct,
        flags
      };
    }

    calculateSkyViewFrontWall({ roofWidth, postCount, sgHeight = 2300, sgGlass = 'clear' }) {
      const requestedRoofWidth = Number(roofWidth);
      const supports = Math.max(2, Number(postCount || 0));
      if (!Number.isFinite(requestedRoofWidth) || requestedRoofWidth <= 0 || !Number.isFinite(supports)) {
        return { ok: false, error: 'INVALID_SKYVIEW_FRONT_WIDTH', requestedRoofWidth, postCount };
      }

      const postWidthMm = Number(this.data?.sideWalls?.sg23?.tdsFrontRule?.postWidthMm || 110);
      const fieldCount = Math.max(1, Math.round(supports) - 1);
      const clearTotalWidthMm = requestedRoofWidth - (Math.round(supports) * postWidthMm);
      const fieldWidthMm = clearTotalWidthMm / fieldCount;
      if (!(fieldWidthMm > 0)) {
        return { ok: false, error: 'SKYVIEW_FRONT_CLEAR_WIDTH_INVALID', requestedRoofWidth, fieldCount, postCount: Math.round(supports), postWidthMm, clearTotalWidthMm };
      }

      const fieldQuote = this.calculateSg23({ width: fieldWidthMm, height: sgHeight, glass: sgGlass, application: 'outdoor', variant: 'auto' });
      if (!fieldQuote.ok) {
        return { ok: false, error: fieldQuote.error, requestedRoofWidth, fieldCount, postCount: Math.round(supports), postWidthMm, clearTotalWidthMm, fieldWidthMm, fieldQuote };
      }

      const vdListNet = fieldQuote.vdListNet * fieldCount;
      const bekEkNet = fieldQuote.bekEkNet * fieldCount;
      const customerGross = fieldQuote.customerGross * fieldCount;
      const vatRate = this.options.vatPct / 100;
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;
      const layoutLabel = fieldCount === 1 ? `1 Frontfeld · ${fieldQuote.layoutLabel}` : `${fieldCount} Frontfelder · je ${fieldQuote.layoutLabel}`;

      return {
        ok: true,
        productCode: 'SKYVIEW',
        requestedRoofWidth,
        split: fieldCount > 1,
        fieldCount,
        postCount: Math.round(supports),
        postWidthMm,
        clearTotalWidthMm,
        fieldWidthMm,
        fieldQuote,
        fields: Array.from({ length: fieldCount }, (_, index) => ({ index: index + 1, ...fieldQuote })),
        layoutLabel,
        widthBand: fieldQuote.widthBand,
        heightBand: fieldQuote.heightBand,
        glass: fieldQuote.glass,
        glassLabel: fieldQuote.glassLabel,
        vdListNet,
        dealerDiscountPct: fieldQuote.dealerDiscountPct,
        bekEkNet,
        customerGross,
        customerNet,
        grossProfitNet,
        rawMarginPct,
        flags: [
          ...(fieldQuote.flags || []),
          { code: 'SKYVIEW_FRONT_BY_SUPPORTS', message: `SkyView Front: ${Math.round(supports)} Pfosten aus der VD-Dachmatrix ergeben ${fieldCount} lichte SG23-Feld${fieldCount === 1 ? '' : 'er'} à ca. ${Math.round(fieldWidthMm)} mm.` }
        ]
      };
    }

    calculateSkyViewSideWall({ width, sgHeight = 2300, sgGlass = 'clear' }) {
      const sg23 = this.calculateSg23({ width, height: sgHeight, glass: sgGlass, application: 'outdoor', variant: 'auto' });
      if (!sg23.ok) return { ok: false, error: sg23.error, sg23 };
      return {
        ok: true,
        productCode: 'SKYVIEW',
        width: Number(width),
        sg23,
        wedge: null,
        layoutLabel: sg23.layoutLabel,
        vdListNet: sg23.vdListNet,
        bekEkNet: sg23.bekEkNet,
        customerGross: sg23.customerGross,
        customerNet: sg23.customerNet,
        grossProfitNet: sg23.grossProfitNet,
        rawMarginPct: sg23.rawMarginPct,
        flags: [
          ...(sg23.flags || []),
          { code: 'SKYVIEW_SIDE_NO_WEDGE', message: 'SkyView kubische Seitenansicht: SG23 wird ohne FR23-Keil kalkuliert.' }
        ]
      };
    }

    calculateTdsSideWall({ width, sgHeight = 2300, sgGlass = 'clear', wedgeLow, wedgeHigh, wedgeGlass = 'clear' }) {
      const sg23 = this.calculateSg23({ width, height: sgHeight, glass: sgGlass, application: 'outdoor', variant: 'auto' });
      if (!sg23.ok) return { ok: false, error: sg23.error, sg23 };

      const wedge = this.calculateFr23Wedge({
        width,
        heightLow: wedgeLow,
        heightHigh: wedgeHigh,
        glass: wedgeGlass,
        standalone: false,
        variant: 'auto'
      });
      if (!wedge.ok) {
        return {
          ok: false,
          error: wedge.error,
          sg23,
          wedge,
          partialCustomerGross: sg23.customerGross,
          flags: [
            ...(sg23.flags || []),
            { code: 'SIDE_WEDGE_PENDING', message: 'Komplette Seitenwand kann erst nach Eingabe der beiden Keilhöhen final bepreist werden.' }
          ]
        };
      }

      const customerGross = sg23.customerGross + wedge.customerGross;
      const vdListNet = sg23.vdListNet + wedge.vdListNet;
      const bekEkNet = sg23.bekEkNet + wedge.bekEkNet;
      const vatRate = this.options.vatPct / 100;
      const customerNet = customerGross / (1 + vatRate);
      const grossProfitNet = customerNet - bekEkNet;
      const rawMarginPct = customerNet > 0 ? (grossProfitNet / customerNet) * 100 : 0;

      return {
        ok: true,
        widthMm: Number(width),
        sg23,
        wedge,
        customerGross,
        customerNet,
        vdListNet,
        bekEkNet,
        grossProfitNet,
        rawMarginPct,
        flags: [...(sg23.flags || []), ...(wedge.flags || [])]
      };
    }

  }

  window.BEKProductEngine = BEKProductEngine;
})();
